import { useEffect, type ReactNode } from "react";
import { motion } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/* ─────────────────────  M3 bottom sheet  ─────────────────────
 * Shared modal that slides up from the bottom. Used by AppShell for
 * the "more" and side-menu sheets, and by module views (e.g. FamList)
 * for forms. Wraps a backdrop, drag-handle, sticky header, and the
 * supplied content. Caller is responsible for the AnimatePresence so
 * the exit animation runs on unmount.
 */

export function M3BottomSheet({
  title,
  children,
  onClose,
  className,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 !mb-0">
      <motion.button
        type="button"
        aria-label="關閉"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
        className={cn(
          "absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-background shadow-2xl pb-safe",
          className,
        )}
      >
        <div className="sticky top-0 z-10 -mb-px flex items-center justify-between border-b border-border/60 bg-background/95 px-4 pb-3 pt-3 backdrop-blur">
          <span
            aria-hidden
            className="absolute inset-x-0 -top-2 mx-auto h-1 w-12 rounded-full bg-foreground/20"
          />
          <h3 className="text-base font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉"
            className="-mr-1 inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-secondary active:bg-secondary/70"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.25 }}
          className="p-4"
        >
          {children}
        </motion.div>
      </motion.div>
    </div>
  );
}
